import {
  type SanityFilter,
  type SanityFilterGroup,
  type SanitySort,
  type SanitySortOptionType,
} from '@data/sanity/queries/types/cart'
import { type SanityCombinedListingSettings } from '@data/sanity/queries/types/modules'
import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { type UrlParameter, usePrevious, useUrlParameters } from './hooks'
import {
  type CollectionProduct,
  getCollectionProducts,
  getUpdatedParameterMapper,
  mergeFilterSlugs,
} from './product-collection'
import { UIContext } from './site-context'

interface ProductCollectionContextProps {
  collectionProducts: CollectionProduct[]
  filterGroups: SanityFilterGroup[]
  defaultSortOption: SanitySortOptionType | null
  activeCollectionUrlParameters: UrlParameter[]
  updateCollectionUrlParameters: (newParameters: UrlParameter[]) => void
}

interface ProductCollectionContextProviderProps {
  products: SanityProductFragment[]
  expandProducts?: boolean
  onlyDiscounts?: boolean
  featuredProductIds: number[]
  combinedListings: SanityCombinedListingSettings[]
  filter?: SanityFilter
  sort?: SanitySort
  children: ReactNode
}

export const ProductCollectionContext =
  createContext<ProductCollectionContextProps>({
    collectionProducts: [],
    filterGroups: [],
    defaultSortOption: null,
    activeCollectionUrlParameters: [],
    updateCollectionUrlParameters: () => {},
  })

export const ProductCollectionContextProvider = ({
  products,
  expandProducts,
  onlyDiscounts,
  featuredProductIds,
  combinedListings,
  filter,
  sort,
  children,
}: ProductCollectionContextProviderProps) => {
  const { isRouteChanging } = useContext(UIContext)

  const collectionProducts = useMemo(() => {
    return getCollectionProducts(
      products,
      featuredProductIds,
      combinedListings,
      expandProducts,
      onlyDiscounts,
    )
  }, [
    combinedListings,
    expandProducts,
    featuredProductIds,
    onlyDiscounts,
    products,
  ])

  const filterGroups = useMemo(() => {
    return (
      filter?.groups
        ?.map((group) => {
          const filterGroup: SanityFilterGroup = {
            ...group,
            options: group.options.filter((option) => {
              return mergeFilterSlugs(products).includes(option.slug.current)
            }),
          }

          return filterGroup
        })
        .filter((group) => group.options.length > 0) ?? []
    )
  }, [filter?.groups, products])

  const defaultSortOption = useMemo(() => {
    return sort?.options?.[0]?.type ?? null
  }, [sort])

  // Manage URL parameters
  const [collectionUrlParameters, setCollectionUrlParameters] =
    useUrlParameters(
      [
        {
          name: 'page',
        },
        {
          name: 'sort',
          value: defaultSortOption,
        },
        {
          name: 'collection',
        },
        ...filterGroups.map((filterGroup) => ({
          name: filterGroup.slug.current,
        })),
      ],
      {
        hideDefaultValue: true,
      },
    )

  // Manage URL parameters
  const previousParameters = usePrevious(collectionUrlParameters)
  const activeCollectionUrlParameters = useMemo(() => {
    return isRouteChanging && previousParameters
      ? previousParameters
      : collectionUrlParameters
  }, [collectionUrlParameters, isRouteChanging, previousParameters])

  // Handle filter and sort updates
  const updateCollectionUrlParameters = useCallback(
    (newParameters: UrlParameter[]) => {
      const updatedParameter = getUpdatedParameterMapper(newParameters)
      const newCollectionUrlParameters =
        activeCollectionUrlParameters.map(updatedParameter)
      setCollectionUrlParameters(newCollectionUrlParameters)
    },
    [activeCollectionUrlParameters, setCollectionUrlParameters],
  )

  return (
    <ProductCollectionContext.Provider
      value={{
        collectionProducts,
        filterGroups,
        defaultSortOption,
        activeCollectionUrlParameters,
        updateCollectionUrlParameters,
      }}
    >
      {children}
    </ProductCollectionContext.Provider>
  )
}
